/**
 * The Community entry's count pill (design "Community chip signal", option G light, Guillaume
 * 2026-10-18) — one rule for the xl nav chip, the header chip below xl (the phone's bubble badge)
 * and the entry card (drawer row + phone home card).
 *
 * - a member with unread notifications → their count, red ("N answers waiting for you"); client
 *   only — the bell's figure, which never exists in server HTML;
 * - else `day ≥ 1` → `day` ("… today"); else `week ≥ 1` → `week` ("… this week");
 * - else (a quiet week, or DealHunt sent no `activity`) → no pill: silence beats a small number,
 *   and never "0";
 * - the day/week pill is also held back once the reader has been to /community (`seen`, Guillaume
 *   2026-10-18): for the rest of that local day, and after it until the count differs from the
 *   one they were shown. The red member pill is never held back — it clears through the bell.
 */
import type { CommunityActivity } from './types'

export type CommunityPillKind = 'day' | 'week' | 'member'

export interface CommunityPill {
  kind: CommunityPillKind
  /** What the pill prints: a number only ("99+" for a capped unread count). */
  count: string
  /** The long words — the chip's `title`, the card's subtitle, the xl chip's accessible name. */
  label: string
  /** The short words for the phone bubble's `aria-label` ("Community, 3 posts today"). */
  short: string
}

const isCount = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 0

/**
 * `activity` as the contract shapes it, or null: non-negative integer counts with `week ≥ day`.
 * Anything else is treated as absent (no pill) rather than printed.
 */
export function communityActivityOf(raw: unknown): CommunityActivity | null {
  if (!raw || typeof raw !== 'object') {
    return null
  }
  const { day, week, as_of: asOf } = raw as Record<string, unknown>
  if (!isCount(day) || !isCount(week) || week < day || typeof asOf !== 'string') {
    return null
  }
  return { day, week, as_of: asOf }
}

function postsLabel(n: number, when: 'today' | 'this week'): string {
  return n === 1
    ? `1 owner review or discussion ${when}`
    : `${n} owner reviews and discussions ${when}`
}

/**
 * The reader's last arrival at /community on this device (localStorage, never sent anywhere):
 * the local calendar day (`YYYY-MM-DD`) and the day/week figure the pill showed then — null when
 * no pill was showing. `kind` is kept for reading the mark; the rule compares `count` only.
 */
export interface CommunitySeenMark {
  date: string
  count: number | null
  kind: 'day' | 'week' | null
}

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/

/** A stored mark as the rule reads it, or null: anything malformed is treated as absent. */
export function communitySeenMarkOf(raw: unknown): CommunitySeenMark | null {
  if (!raw || typeof raw !== 'object') {
    return null
  }
  const { date, count, kind } = raw as Record<string, unknown>
  if (typeof date !== 'string' || !DAY_RE.test(date)) {
    return null
  }
  if (count !== null && !isCount(count)) {
    return null
  }
  if (kind !== null && kind !== 'day' && kind !== 'week') {
    return null
  }
  return { date, count, kind }
}

/** The local calendar day of `now` as `YYYY-MM-DD` — the reader's day, not UTC's. */
export function localDay(now: Date): string {
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${mm}-${dd}`
}

/** The been-to-Community rule's input: the stored mark and the reader's `localDay` — together,
 *  so a mark can never be judged against a missing day (which would read as "an earlier day"). */
export interface CommunitySeen {
  mark: CommunitySeenMark | null
  today: string
}

/**
 * Pure. `unread` = the bell's count (0 / null for a visitor or before mount). `seen` applies the
 * been-to-Community rule; without it the pill is the plain figure.
 */
export function communityPill(
  activity: CommunityActivity | null | undefined,
  unread: number | null | undefined,
  capped = false,
  seen: CommunitySeen | null = null
): CommunityPill | null {
  const pill = rawCommunityPill(activity, unread, capped)
  if (!pill || pill.kind === 'member' || !seen?.mark) {
    return pill
  }
  if (seen.mark.date === seen.today || seen.mark.count === Number(pill.count)) {
    return null
  }
  return pill
}

function rawCommunityPill(
  activity: CommunityActivity | null | undefined,
  unread: number | null | undefined,
  capped: boolean
): CommunityPill | null {
  if (unread && unread > 0) {
    const count = capped ? '99+' : String(unread)
    const words =
      unread === 1 && !capped ? '1 answer waiting for you' : `${count} answers waiting for you`
    return { kind: 'member', count, label: words, short: words }
  }
  if (!activity) {
    return null
  }
  if (activity.day >= 1) {
    const n = activity.day
    return {
      kind: 'day',
      count: String(n),
      label: postsLabel(n, 'today'),
      short: `${n} ${n === 1 ? 'post' : 'posts'} today`,
    }
  }
  if (activity.week >= 1) {
    const n = activity.week
    return {
      kind: 'week',
      count: String(n),
      label: postsLabel(n, 'this week'),
      short: `${n} ${n === 1 ? 'post' : 'posts'} this week`,
    }
  }
  return null
}
